import { useMemo } from "react";
import { AxiosRequestConfig } from "axios";
import type { SortingState } from "@tanstack/react-table";
import { axiosInstance } from "../../utils";
import type { CandidateColumnFilter } from "./useCandidatesPage";

export interface ExportFilterDescription {
    label: string;
    value: string;
}

export type CandidateExportFormat = "xlsx" | "csv" | "json";

export interface CandidatesExportRequest {
    entityId?: string;
    sort?: SortingState;
    filters?: Record<string, CandidateColumnFilter>;
    qualificationFilter?: string;
    qualificationFilterMode?: "and" | "or";
    format: CandidateExportFormat;
    filterDescriptions?: ExportFilterDescription[];
}

export function useCandidatesExport() {
    const axios = axiosInstance();

    return useMemo(
        () =>
            async (clientId: string, request: CandidatesExportRequest, progressCallback?: (progress: number) => void): Promise<Blob> => {
                const config: AxiosRequestConfig = {
                    responseType: "blob",
                };
                if (progressCallback) {
                    config.onDownloadProgress = progressEvent => {
                        const loaded = progressEvent.loaded;
                        const total = progressEvent.total;
                        if (total) {
                            progressCallback((loaded / total) * 100);
                        }
                    };
                }

                const body = {
                    entityId: request.entityId ? request.entityId : undefined,
                    sort: request.sort?.map(s => ({ id: s.id, desc: s.desc })),
                    filters: request.filters && Object.keys(request.filters).length > 0 ? request.filters : undefined,
                    qualificationFilter: request.qualificationFilter,
                    qualificationFilterMode: request.qualificationFilterMode,
                    format: request.format,
                    filterDescriptions: request.filterDescriptions,
                };

                const response = await axios.post<Blob>(`api/clients/${clientId}/candidates/export`, body, config);
                return response.data;
            },
        [axios]
    );
}
